import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { CheckCircleFilled } from '@ant-design/icons'
import { Alert, Button, Flex, Form, Input, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { redeemOnAccount, type AccountVoucherInput } from '@/features/portal/api'
import { accountVoucherOutcome, type AccountVoucherOutcome } from '@/features/portal/accountVoucher'
import type { EntryMethod, VoucherCard } from '@/features/portal/voucherCard'
import { errorStatusAndTag, translatePortalError } from '@/shared/api/errors'
import { StepHeader } from '@/shared/ui/StepHeader'
import { CardEntry } from './card/CardEntry'

interface AccountVoucherStepProps {
  zoneId: string
  mac: string
  ip: string | null
  nasIdentifier: string
  onRedeemed: (outcome: AccountVoucherOutcome, login: string) => void
  onBack: () => void
}

interface ScannedCard {
  card: VoucherCard
  entryMethod: EntryMethod
}

export function AccountVoucherStep({ zoneId, mac, ip, nasIdentifier, onRedeemed, onBack }: AccountVoucherStepProps) {
  const { t } = useTranslation()
  const [login, setLogin] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [scanned, setScanned] = useState<ScannedCard | null>(null)
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({ mutationFn: (input: AccountVoucherInput) => redeemOnAccount(input) })

  const typedCode = code.replace(/\s+/g, '')
  const chosen: ScannedCard | null =
    scanned ?? (typedCode ? { card: { kind: 'code', code: typedCode }, entryMethod: 'CODE' } : null)

  const handleSubmit = (): void => {
    const fail = (key: string): void => setError(t(key))
    if (!login.trim()) return fail('account.loginRequired')
    if (!password) return fail('account.passwordRequired')
    if (!chosen) return fail('account.cardRequired')
    setError(null)
    mutation.mutate(
      { zoneId, mac, ip, nasIdentifier, login, password, card: chosen.card, entryMethod: chosen.entryMethod },
      {
        onSuccess: (view) => onRedeemed(accountVoucherOutcome(view, password), view.login),
        onError: (err) => {
          const { status } = errorStatusAndTag(err)
          if (status === 401) setPassword('')
          if (status === 409) setScanned(null)
          setError(translatePortalError(err, t).message)
        },
      },
    )
  }

  return (
    <Flex vertical gap={16}>
      <StepHeader title={t('account.title')} subtitle={t('account.subtitle')} />

      {error ? <Alert type="error" showIcon message={error} /> : null}

      <Form layout="vertical" onFinish={handleSubmit} autoComplete="off">
        <Form.Item label={t('account.login')} style={{ marginBottom: 12 }}>
          <Input
            size="large"
            value={login}
            onChange={(event) => setLogin(event.target.value)}
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            dir="ltr"
            disabled={mutation.isPending}
          />
        </Form.Item>
        <Form.Item label={t('account.password')} style={{ marginBottom: 16 }}>
          <Input.Password
            size="large"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            dir="ltr"
            disabled={mutation.isPending}
          />
        </Form.Item>

        <Flex vertical gap={10} style={{ marginBottom: 16 }}>
          {scanned ? (
            <Flex align="center" justify="space-between" gap={8}>
              <Typography.Text type="success">
                <CheckCircleFilled /> {t('card.scanned')}
              </Typography.Text>
              <Button type="link" onClick={() => setScanned(null)} disabled={mutation.isPending}>
                {t('card.change')}
              </Button>
            </Flex>
          ) : (
            <CardEntry
              code={code}
              onCodeChange={setCode}
              onCard={(card, entryMethod) => setScanned({ card, entryMethod })}
              disabled={mutation.isPending}
            />
          )}
        </Flex>

        <Flex vertical gap={8}>
          <Button type="primary" htmlType="submit" size="large" block loading={mutation.isPending}>
            {mutation.isPending ? t('account.submitting') : t('account.submit')}
          </Button>
          <Button type="text" block onClick={onBack} disabled={mutation.isPending}>
            {t('common.back')}
          </Button>
        </Flex>
      </Form>
    </Flex>
  )
}
